import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Settings, CheckCheck, Inbox, Archive, RefreshCw, X, Trash2, Save, ExternalLink, RotateCcw } from 'lucide-react';
import api, { API_BASE } from '../services/api'; 

function Notifications({ onNavigate, onUnreadCountChange }) {
  const [tab, setTab] = useState('All');
  const [allNotifs, setAllNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Preferences state
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({ emailNotifications: true, inAppNotifications: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const fetchNotifications = async (pageNum = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      const res = await fetch(`${API_BASE}/notifications?page=${pageNum}&limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.success && data.notifications) {
        setAllNotifs(prev => append ? [...prev, ...data.notifications] : data.notifications);
        setTotal(data.total);
        setHasMore(data.notifications.length === 20);
        setPage(pageNum);
        if (onUnreadCountChange && data.unreadCount !== undefined) {
          onUnreadCountChange(data.unreadCount);
        }
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API_BASE}/me/preferences`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data) {
        setPreferences({
          emailNotifications: data.emailNotifications ?? true,
          inAppNotifications: data.inAppNotifications ?? true
        });
      }
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  };

  const savePreferences = async () => {
    try {
      setSavingPrefs(true);
      const res = await fetch(`${API_BASE}/me/preferences`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      if (res.ok) {
        setShowPreferences(false);
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();

    // Setup SSE for real-time notifications
    const token = localStorage.getItem('token');
    if (!token) return;

    let eventSource;
    // Append token as query parameter so backend can authenticate the stream if we write a middleware for it
    eventSource = new EventSource(`${API_BASE}/notifications/stream?token=${token}`);
    
    eventSource.onmessage = (event) => {
      try {
        const newNotif = JSON.parse(event.data);
        setAllNotifs(prev => [newNotif, ...prev]);
        setTotal(prev => prev + 1);
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = (err) => {
      // console.error('SSE Error:', err);
      eventSource.close();
    };

    return () => {
      if (eventSource) eventSource.close();
    };
  }, []);

  const markAsRead = async (id) => {
    try {
      setAllNotifs(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      await fetch(`${API_BASE}/notifications/${id}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      setAllNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
      await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const archiveNotification = async (id) => {
    try {
      setAllNotifs(prev => prev.map(n => n.id === id ? { ...n, status: 'ARCHIVED' } : n));
      await fetch(`${API_BASE}/notifications/${id}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error archiving notification:', err);
    }
  };

  const unarchiveNotification = async (id) => {
    try {
      setAllNotifs(prev => prev.map(n => n.id === id ? { ...n, status: 'ACTIVE' } : n));
      await fetch(`${API_BASE}/notifications/${id}/unarchive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error unarchiving notification:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      setAllNotifs(prev => prev.filter(n => n.id !== id));
      await fetch(`${API_BASE}/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm("Are you sure you want to permanently delete all your notifications?")) return;
    try {
      setAllNotifs([]);
      await fetch(`${API_BASE}/notifications/clear-all`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      console.error('Error clearing all notifications:', err);
    }
  };

  const notifs = tab === 'All' 
    ? allNotifs.filter(n => n.status !== 'ARCHIVED') 
    : tab === 'Unread' 
      ? allNotifs.filter(n => !n.isRead && n.status !== 'ARCHIVED') 
      : allNotifs.filter(n => n.status === 'ARCHIVED');

  const timeAgo = (dateStr) => {
    const ms = new Date() - new Date(dateStr);
    const min = Math.floor(ms / 60000);
    if (min < 1) return `Just now`;
    if (min < 60) return `${min} minutes ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs} hours ago`;
    return `${Math.floor(hrs / 24)} days ago`;
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--text-main)', margin: '0 0 0.5rem 0' }}>Notifications</h2>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>Stay updated on your deployments and team activities.</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={() => fetchNotifications(1, false)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-main)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
          <button onClick={() => setShowPreferences(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-main)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', transition: 'all 0.2s' }}>
            <Settings size={16} /> Preferences
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['All', 'Unread', 'Archived'].map(t => {
            const count = t === 'Unread' 
              ? allNotifs.filter(n => !n.isRead && n.status !== 'ARCHIVED').length 
              : t === 'Archived' 
                ? allNotifs.filter(n => n.status === 'ARCHIVED').length
                : allNotifs.filter(n => n.status !== 'ARCHIVED').length;
                
            return (
              <button 
                key={t}
                onClick={() => setTab(t)}
                style={{ padding: '0.5rem 1rem', background: tab === t ? 'var(--bg-card)' : 'transparent', color: tab === t ? 'var(--text-main)' : 'var(--text-muted)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s', boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {t} <span style={{ background: tab === t ? 'var(--primary)' : 'var(--bg-main)', color: tab === t ? 'white' : 'var(--text-muted)', padding: '2px 6px', borderRadius: '10px', fontSize: '0.7rem', marginLeft: '0.25rem' }}>{count}</span>
              </button>
            )
          })}
        </div>
        
        <div style={{ display: 'flex', gap: '1rem' }}>
          {tab !== 'Archived' && (
            <button onClick={markAllAsRead} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', transition: 'color 0.2s' }}>
              <CheckCheck size={16} /> Mark all as read
            </button>
          )}
          <button onClick={clearAllNotifications} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', color: 'var(--danger, #ef4444)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500', transition: 'color 0.2s' }}>
            <Trash2 size={16} /> Clear All
          </button>
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
        {loading && allNotifs.length === 0 ? (
          <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
             Loading...
          </div>
        ) : notifs.length === 0 ? (
          <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Inbox size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text-main)' }}>You're all caught up!</h3>
            <p style={{ margin: 0 }}>No {tab.toLowerCase()} notifications to show right now.</p>
          </div>
        ) : (
          <>
            {notifs.map((n, i) => (
              <div key={n.id} style={{ padding: '1.5rem', borderBottom: i < notifs.length - 1 ? '1px solid var(--border-color)' : 'none', display: 'flex', gap: '1rem', alignItems: 'flex-start', background: n.isRead ? 'transparent' : 'var(--bg-main)', transition: 'background 0.2s' }}>
                <div style={{ marginTop: '0.25rem' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: n.type === 'error' ? 'var(--danger, #ef4444)' : n.type === 'success' ? 'var(--success, #10b981)' : 'var(--primary)', boxShadow: !n.isRead ? `0 0 0 4px ${n.type === 'error' ? 'rgba(239,68,68,0.2)' : n.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(59,130,246,0.2)'}` : 'none' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: n.isRead ? '500' : '700', color: 'var(--text-main)', margin: '0 0 0.25rem' }}>{n.title}</h4>
                  <p style={{ color: 'var(--text-muted)', margin: '0 0 0.75rem', fontSize: '0.9rem', lineHeight: '1.5' }}>
                    {n.message && n.message.includes('custom_')
                      ? n.message.replace(/\(custom_[^)]+\)/gi, '(Pro Plan)').replace(/custom_\w+/gi, 'Pro Plan')
                      : n.message}
                  </p>
                  {n.link && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <a
                        href={n.link}
                        onClick={(e) => {
                          if (onNavigate && n.link.startsWith('/')) {
                            e.preventDefault();
                            onNavigate(n.link.replace(/^\//, ''));
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: 'var(--primary, #3b82f6)',
                          background: 'rgba(59, 130, 246, 0.1)',
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          textDecoration: 'none'
                        }}
                      >
                        {n.link.includes('billing') || n.link.includes('plan') ? (
                          <>Upgrade Plan <ExternalLink size={13} /></>
                        ) : n.link.includes('support') || n.link.includes('ticket') ? (
                          <>View Ticket <ExternalLink size={13} /></>
                        ) : (
                          <>View Details <ExternalLink size={13} /></>
                        )}
                      </a>
                    </div>
                  )}
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: '500' }}>{timeAgo(n.createdAt)}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {!n.isRead && (
                    <button onClick={() => markAsRead(n.id)} style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }} title="Mark as read">
                      <CheckCheck size={16} />
                    </button>
                  )}
                  {n.status !== 'ARCHIVED' ? (
                    <button onClick={() => archiveNotification(n.id)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }} title="Archive">
                      <Archive size={16} />
                    </button>
                  ) : (
                    <button onClick={() => unarchiveNotification(n.id)} style={{ background: 'transparent', border: 'none', color: 'var(--primary, #3b82f6)', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }} title="Unarchive / Restore to Inbox">
                      <RotateCcw size={16} />
                    </button>
                  )}
                  <button onClick={() => deleteNotification(n.id)} style={{ background: 'transparent', border: 'none', color: 'var(--danger, #ef4444)', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }} title="Permanently Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {hasMore && tab === 'All' && (
              <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid var(--border-color)' }}>
                <button 
                  onClick={() => fetchNotifications(page + 1, true)} 
                  style={{ background: 'var(--bg-main)', border: '1px solid var(--border-color)', color: 'var(--text-main)', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Preferences (Rendered via React Portal at Document Body to prevent sidebar overlap & transparency) */}
      {showPreferences && createPortal(
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.6)', 
            backdropFilter: 'blur(4px)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 99999,
            padding: '1rem',
            boxSizing: 'border-box'
          }}
          onClick={() => setShowPreferences(false)}
        >
          <div 
            style={{ 
              position: 'relative',
              background: 'var(--bg-card, #ffffff)', 
              border: '1px solid var(--border-color, rgba(0, 0, 0, 0.1))',
              color: 'var(--text-main, #0f172a)',
              padding: '2rem', 
              borderRadius: '16px', 
              width: '100%', 
              maxWidth: '440px', 
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Right X Close Button */}
            <button 
              onClick={() => setShowPreferences(false)} 
              title="Close"
              style={{ 
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                background: 'var(--surface-hover, rgba(0, 0, 0, 0.05))', 
                border: '1px solid var(--border-color, rgba(0, 0, 0, 0.1))', 
                color: 'var(--text-muted, #64748b)', 
                cursor: 'pointer', 
                padding: '0.4rem', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--surface-hover, rgba(0, 0, 0, 0.05))';
                e.currentTarget.style.color = 'var(--text-muted, #64748b)';
                e.currentTarget.style.borderColor = 'var(--border-color, rgba(0, 0, 0, 0.1))';
              }}
            >
              <X size={18} />
            </button>

            <div style={{ marginBottom: '1.5rem', paddingRight: '2rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main, #0f172a)', fontWeight: '700' }}>Notification Preferences</h3>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted, #64748b)' }}>Manage how you receive alerts and updates.</p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', color: 'var(--text-main, #0f172a)', fontSize: '0.95rem', fontWeight: '500' }}>
                <input 
                  type="checkbox" 
                  checked={preferences.inAppNotifications} 
                  onChange={(e) => setPreferences({...preferences, inAppNotifications: e.target.checked})}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--primary, #6366f1)', cursor: 'pointer' }}
                />
                Receive In-App Notifications
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', color: 'var(--text-main, #0f172a)', fontSize: '0.95rem', fontWeight: '500' }}>
                <input 
                  type="checkbox" 
                  checked={preferences.emailNotifications} 
                  onChange={(e) => setPreferences({...preferences, emailNotifications: e.target.checked})}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--primary, #6366f1)', cursor: 'pointer' }}
                />
                Receive Email Alerts
              </label>
            </div>

            <button 
              onClick={savePreferences} 
              disabled={savingPrefs}
              style={{ width: '100%', background: 'var(--primary, #6366f1)', color: 'white', border: 'none', padding: '0.75rem', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}
            >
              <Save size={16} /> {savingPrefs ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default Notifications;
